/**
 * Analytics Utility for Web Traffic Tracking
 * Provides simple functions to track user behavior across the application
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL !== undefined ? process.env.REACT_APP_BACKEND_URL : 'http://localhost:8001';

/**
 * Event Types Constants - matches backend EventTypes
 */
export const EventTypes = {
  // Page views
  PAGE_VIEW_LANDING: 'page_view_landing',
  PAGE_VIEW_DASHBOARD: 'page_view_dashboard',
  PAGE_VIEW_SESSION_SETUP: 'page_view_session_setup',
  PAGE_VIEW_SUBSCRIPTION: 'page_view_subscription',
  PAGE_VIEW_PROFILE: 'page_view_profile',
  PAGE_VIEW_SESSION: 'page_view_session',
  
  // Landing page clicks
  CLICK_FEATURES: 'click_features',
  CLICK_USE_CASES: 'click_use_cases',
  CLICK_PRICING: 'click_pricing',
  CLICK_SIGN_IN: 'click_sign_in',
  CLICK_GET_STARTED: 'click_get_started',
  CLICK_START_FREE: 'click_start_free',
  CLICK_START_PREMIUM: 'click_start_premium',
  
  // Auth events
  SUCCESSFUL_SIGN_IN: 'successful_sign_in',
  SUCCESSFUL_SIGN_UP: 'successful_sign_up',
  
  // Dashboard events
  CLICK_NEW_SESSION: 'click_new_session',
  
  // Session setup events
  CLICK_IDEA_POOL: 'click_idea_pool',
  CLICK_QUESTIONNAIRE: 'click_questionnaire',
  REDIRECT_TO_SUBSCRIPTION_IDEA: 'redirect_subscription_idea_pool',
  REDIRECT_TO_SUBSCRIPTION_QUEST: 'redirect_subscription_questionnaire',
  CLICK_CREATE_SESSION_IDEA: 'click_create_session_idea_pool',
  CLICK_CREATE_QUESTIONNAIRE: 'click_create_questionnaire',
  
  // Subscription events
  CLICK_UPGRADE: 'click_upgrade',
  SUCCESSFUL_PAYMENT: 'successful_payment',
  
  // Session events
  SESSION_CREATED: 'session_created',
  SESSION_STARTED: 'session_started',
  SESSION_COMPLETED: 'session_completed',
  PARTICIPANT_JOINED: 'participant_joined',
  PARTICIPANT_SUBMITTED: 'participant_submitted',
};

/**
 * Track an analytics event
 * 
 * @param {string} eventType - Type of event from EventTypes
 * @param {string} pageUrl - Current page URL
 * @param {Object} options - Additional options
 * @param {string} options.action - Description of action taken
 * @param {string} options.sessionId - Session ID if applicable
 * @param {string} options.sessionStatus - Session status if applicable
 * @param {number} options.participantCount - Number of participants
 * @param {number} options.submissionCount - Number of submissions
 * @param {number} options.totalCharacters - Total characters submitted
 * @param {Object} options.metadata - Additional metadata
 */
export const trackEvent = async (eventType, pageUrl, options = {}) => {
  try {
    const token = localStorage.getItem('token');
    
    const requestBody = {
      event_type: eventType,
      page_url: pageUrl,
      action: options.action || null,
      session_id: options.sessionId || null,
      session_status: options.sessionStatus || null,
      participant_count: options.participantCount || null,
      submission_count: options.submissionCount || null,
      total_characters: options.totalCharacters || null,
      referrer: document.referrer || null,
      metadata: options.metadata || null,
    };

    const headers = {
      'Content-Type': 'application/json',
    };

    // Add auth header if user is logged in
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Send analytics event (fire and forget - don't block user experience)
    fetch(`${BACKEND_URL}/api/analytics/track`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    }).catch(err => {
      // Silently fail - analytics should never break the app
      console.debug('Analytics tracking failed:', err);
    });
  } catch (error) {
    // Silently fail - analytics should never break the app
    console.debug('Analytics error:', error);
  }
};

/**
 * Track a page view
 * Automatically called when page loads
 * 
 * @param {string} pageType - Type of page (landing, dashboard, etc.)
 */
export const trackPageView = (pageType) => {
  const pageUrl = window.location.href;
  let eventType;
  
  switch (pageType) {
    case 'landing':
      eventType = EventTypes.PAGE_VIEW_LANDING;
      break;
    case 'dashboard':
      eventType = EventTypes.PAGE_VIEW_DASHBOARD;
      break;
    case 'session_setup':
      eventType = EventTypes.PAGE_VIEW_SESSION_SETUP;
      break;
    case 'subscription':
      eventType = EventTypes.PAGE_VIEW_SUBSCRIPTION;
      break;
    case 'profile':
      eventType = EventTypes.PAGE_VIEW_PROFILE;
      break;
    case 'session':
      eventType = EventTypes.PAGE_VIEW_SESSION;
      break;
    default:
      eventType = 'page_view_unknown';
  }
  
  trackEvent(eventType, pageUrl);
};

/**
 * Track a button click
 * 
 * @param {string} buttonName - Name/description of the button
 * @param {string} eventType - Specific event type from EventTypes
 */
export const trackClick = (buttonName, eventType) => {
  const pageUrl = window.location.href;
  trackEvent(eventType, pageUrl, { action: `clicked ${buttonName}` });
};

/**
 * Track session events with statistics
 * 
 * @param {string} eventType - Session event type
 * @param {Object} sessionData - Session data
 */
export const trackSessionEvent = (eventType, sessionData) => {
  const pageUrl = window.location.href;
  trackEvent(eventType, pageUrl, {
    sessionId: sessionData.id,
    sessionStatus: sessionData.status,
    participantCount: sessionData.participant_count,
    submissionCount: sessionData.submission_count,
    totalCharacters: sessionData.total_characters,
  });
};

/**
 * Hook to track page view on component mount
 * Usage: usePageTracking('dashboard');
 */
export const usePageTracking = (pageType) => {
  // Track page view on mount
  React.useEffect(() => {
    trackPageView(pageType);
  }, [pageType]);
};
